import { EventContext } from "@/domains/metrics/context";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { MDSButton, MDSButtonVariant, MDSSpacer } from "@/mds";
import { uuidModule } from "@/modules/uuid";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { AppStore } from "@/store";
import { getMentionedNoteIds } from "@/store/note/metadata";
import {
  NoteContentDocumentModelData,
  NoteContentDocumentUpsertedSyncUpdateValue,
  NoteMediaKind,
  NoteModelData,
  NoteUpsertedSyncUpdateValue,
} from "@/store/note/types";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { AbstractSyncErrorModalFieldsGenerator } from "@/store/sync/operations/errors/SyncErrorModalFields";
import { IUpdateNoteContentOverwritingFullStateDestructivelyOperation } from "@/store/sync/operations/types";
import {
  SyncCustomErrorData,
  SyncModelData,
  OptimisticSyncUpdate,
  SyncOperationKind,
} from "@/store/sync/types";
import { ExtractedNoteMention } from "@mem-labs/common-editor";
import { uniq } from "lodash-es";
import { runInAction } from "mobx";
import { JsonValue } from "type-fest";

export class UpdateNoteContentOverwritingFullStateDestructivelyOperation extends BaseSyncOperation<IUpdateNoteContentOverwritingFullStateDestructivelyOperation> {
  primaryLabel: string;
  protected secondaryLabel: string;
  protected mediaKinds?: NoteMediaKind[];
  protected transactionMetadata?: Record<string, JsonValue>;
  protected mentions?: ExtractedNoteMention[];
  protected isEmpty?: boolean;

  get operationKind(): SyncOperationKind {
    return "UPDATE_NOTE_CONTENT_OVERWRITING_FULL_STATE_DESTRUCTIVELY";
  }

  constructor({
    primaryLabel,
    secondaryLabel,
    mediaKinds,
    mentions,
    isEmpty,
    ...params
  }: {
    primaryLabel: string;
    secondaryLabel: string;
    mediaKinds?: NoteMediaKind[];
    mentions?: ExtractedNoteMention[];
    isEmpty?: boolean;
  } & BaseSyncOperationParams<IUpdateNoteContentOverwritingFullStateDestructivelyOperation>) {
    super(params);
    this.primaryLabel = primaryLabel;
    this.secondaryLabel = secondaryLabel;
    this.mediaKinds = mediaKinds;
    this.mentions = mentions;
    this.isEmpty = isEmpty;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const noteContentDocumentId = resolveNoteContentDocumentSyncModelUuid({
      noteId: this.payload.id,
    });

    const note = await this.store.notes.getAsync(this.payload.id);
    const noteContentDocument =
      await this.store.noteContentDocuments.getAsync(noteContentDocumentId);

    if (!note || !noteContentDocument) return [];

    const modifiedBySpaceAccountIds = [...note.modelData.modified_by_space_account_ids];

    const noteValue: NoteUpsertedSyncUpdateValue = {
      model_id: note.id,
      model_kind: "NOTE",
      model_version: note.modelVersion,
      model_data: {
        ...note.modelData,
        primary_label: this.primaryLabel,
        secondary_label: this.secondaryLabel,
        media_kinds: this.mediaKinds || [],
        mentioned_note_ids: getMentionedNoteIds(this.mentions || []),
        locally_modified_at: this.committedAt,
        modified_by_space_account_ids: uniq(modifiedBySpaceAccountIds),
        is_empty: this.isEmpty || false,
      },
      model_scopes: note.modelScopes,
    };

    const noteSyncUpdate: OptimisticSyncUpdate<NoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteValue,
    };

    const noteContentValue: NoteContentDocumentUpsertedSyncUpdateValue = {
      model_id: noteContentDocument.id,
      model_kind: "NOTE_CONTENT_DOCUMENT",
      model_version: noteContentDocument.modelVersion,
      model_data: {
        note_id: note.id,
        encoded_content: this.payload.encoded_content,
      },
      model_scopes: noteContentDocument.modelScopes,
    };

    const noteContentSyncUpdate: OptimisticSyncUpdate<NoteContentDocumentModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteContentValue,
    };

    return [noteContentSyncUpdate, noteSyncUpdate];
  }

  async execute(): Promise<void> {
    return super.execute();
  }

  async triggerRecompute() {
    const noteContentDocumentId = resolveNoteContentDocumentSyncModelUuid({
      noteId: this.payload.id,
    });
    await this.store.notes.recompute(this.payload.id);
    await this.store.noteContentDocuments.recompute(noteContentDocumentId);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.triggerModal(
      this.getSyncErrorModalFieldsGenerator("INVALID", {
        requestAccessAndSaveEditsInNewNoteButtonsEnabled: true,
      })
    );
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.triggerModal(
      this.getSyncErrorModalFieldsGenerator("PERMISSION_DENIED", {
        requestAccessAndSaveEditsInNewNoteButtonsEnabled: true,
      })
    );
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.triggerModal(
      this.getSyncErrorModalFieldsGenerator("UNKNOWN", {
        requestAccessAndSaveEditsInNewNoteButtonsEnabled: true,
      }),
      SyncErrorHandlingType.RetryWithLimit
    );
  }

  get syncErrorModalFieldsGenerator() {
    return this.getSyncErrorModalFieldsGenerator("UNKNOWN");
  }

  getSyncErrorModalFieldsGenerator(
    kind: SyncCustomErrorData["kind"],
    opts: UpdateNoteContentUsingDiffSyncErrorModalFieldsGeneratorOps = {
      requestAccessAndSaveEditsInNewNoteButtonsEnabled: false,
    }
  ) {
    return (store: AppStore) =>
      new UpdateNoteContentUsingDiffSyncErrorModalFieldsGenerator(
        store,
        this.id,
        this.payload.id,
        kind,
        opts
      );
  }
}

interface UpdateNoteContentUsingDiffSyncErrorModalFieldsGeneratorOps {
  requestAccessAndSaveEditsInNewNoteButtonsEnabled: boolean;
  onAfterClose?: () => void;
}

class UpdateNoteContentUsingDiffSyncErrorModalFieldsGenerator extends AbstractSyncErrorModalFieldsGenerator {
  protected id: string;
  protected noteId: string;
  protected kind: SyncCustomErrorData["kind"];
  protected requestAccessAndSaveEditsInNewNoteButtonsEnabled: boolean;
  protected onAfterClose?: () => void;

  constructor(
    store: AppStore,
    id: string,
    noteId: string,
    kind: SyncCustomErrorData["kind"],
    opts: UpdateNoteContentUsingDiffSyncErrorModalFieldsGeneratorOps
  ) {
    super(store);

    this.id = id;
    this.noteId = noteId;
    this.kind = kind;
    this.requestAccessAndSaveEditsInNewNoteButtonsEnabled =
      opts.requestAccessAndSaveEditsInNewNoteButtonsEnabled;
    this.onAfterClose = opts.onAfterClose;
  }

  reloadEditors() {
    // Any open editor or note viewer will be notified to reload the note without reverted operations.
    this.store.publicAppStore.interface.notifyReloadRequired(this.noteId);
    this.onAfterClose?.();
  }

  requestEditAccess = () => {
    // TODO: https://linear.app/mem-labs/issue/MEM-7702/update-note-contents-permission-denied-request-edit-access
  };

  discardEdits = async () => {
    await this.store.sync.actionQueue.skipAndRevertRelatedOperationsById(this.id);
    runInAction(() => {
      this.reloadEditors();
      this.store.sync.actionQueue.resume();
    });
  };

  saveChangesAsNewNote = async () => {
    // TODO: https://linear.app/mem-labs/issue/MEM-7703/update-note-contents-permission-denied-save-edits-as-new-note
    const note = await this.store.notes.getAsync(this.noteId);
    if (!note) return;
    await note.saveAsNewNote({ eventContext: EventContext.ErrorModal });
    this.discardEdits();
  };

  tryAgain = () => {
    this.store.sync.actionQueue.resume();
  };

  get title() {
    const note = this.store.notes.get(this.noteId);
    const title = note?.title || UNTITLED_NOTE_TITLE;
    return `Having trouble saving your “${title}”`;
  }

  get message() {
    switch (this.kind) {
      case "INVALID":
      case "UNKNOWN": {
        return `Trying again usually solves this issue. If this issue continues to appear, you can save your changes as a new note instead.`;
      }
      case "PERMISSION_DENIED": {
        return (
          <>
            You no longer have edit access to this note and your changes could not be saved. Contact
            the note owner, or <span onClick={this.requestEditAccess}>request edit access</span>,
            then try again.
          </>
        );
      }
    }
  }

  get resetActionLabel() {
    return this.requestAccessAndSaveEditsInNewNoteButtonsEnabled ? null : "Got it";
  }

  get extraActionButtons() {
    if (!this.requestAccessAndSaveEditsInNewNoteButtonsEnabled) return;

    return (
      <>
        <MDSButton onClick={this.discardEdits}>Discard edits</MDSButton>
        <MDSSpacer />
        <MDSButton onClick={this.saveChangesAsNewNote}>Save changes as new note</MDSButton>
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={this.tryAgain}>
          Try again
        </MDSButton>
      </>
    );
  }

  modalActionHandler = async () => {
    await this.store.sync.actionQueue.skipAndRevertRelatedOperationsById(this.id);
    this.reloadEditors();
  };
}
